import instance from 'api';
import { AxiosRequestConfig } from 'axios';
import { IApplicationModel, IDefectModel, IExecutorModel, IHistoryModel, IRealEstateModel } from 'interfaces';
import {
  IStatusReason,
  ICallback,
  IDelayReason,
  IDoneResponse,
  ISmartRemont,
  IRealEstateDefectType,
  ICapexApplication,
  ICapexConstructive,
  ICapexProgress,
  IRateClientByExecutor,
  INPSApplication,
  ISendToSignUsoApplication,
  IChangeUsoApplicationStatus,
  IRejectUsoApplication,
  INPSScore,
  IApplicationObserver,
  IApplicationObserverCandidate,
} from './interfaces';

export const extendApplicationDate = (data: {
  applicationNumber: string;
  comment: string;
  delayDays: string;
  delayReasonId: string;
  showBigApp: boolean;
}) => {
  return instance()
    .post('/application/delay', data)
    .then((response) => response.data);
};

export const getChangeExecutorReason = () => {
  return instance()
    .post('/dictionary/changeExecutorReasons', {})
    .then((response) => response.data);
};

export const changeApplicationExecutor = (data: {
  applicationNumber: string;
  changeExecutorReasonId: number;
  comment: string;
  newExecutorId: string;
  newExecutorPositionId: number;
}) => {
  return instance()
    .post('/application/v3/changeExecutor', data)
    .then((response) => response.data);
};

export const saveNPSApplication = (applicationNumber: string, npsApplication: INPSApplication) => {
  return instance()
    .post<IApplicationModel>(`/application/v3/${applicationNumber}/save`, npsApplication)
    .then((response) => response.data);
};

export const saveApplicationQuestionable = (data: {
  applicationNumber: string;
  comment: string;
  applicationQuestionableId: number;
}) => {
  return instance()
    .post('/application/saveQuestionable', data)
    .then((response) => response.data);
};

export const removeApplicationQuestionable = (data: {
  applicationNumber: string;
  comment: string;
  applicationQuestionableId: number;
}) => {
  return instance()
    .post('/application/removeQuestionable', data)
    .then((response) => response.data);
};

export const takeApplication = (data: {
  applicationNumber: string;
  changeExecutorReasonId: number;
  comment: string;
}) => {
  return instance()
    .post('/application/v2/take', data)
    .then((response) => response.data);
};

export const delayClientApplication = (data: {
  applicationNumber: string;
  delayTimestamp: string;
  photoList: string[];
  delayReasonClientId: number;
  comment: string;
}) => {
  return instance()
    .post('/application/delayClient', data)
    .then((response) => response.data);
};

export const removeDelayClientApplication = (data: { applicationNumber: string; comment: string }) => {
  return instance()
    .post('/application/delayRemoveClient', data)
    .then((response) => response.data);
};

export const getChangeStatusReason = (statusId: number) => {
  return instance()
    .post('/dictionary/changeStatusReasons', { statusId })
    .then((response) => response.data.filter((item: IStatusReason) => !item.forMobile));
};

export const getApplicationHistory = (applicationNumber: string) => {
  return instance()
    .post('/application/v4/history', { applicationNumber })
    .then((response) => response.data);
};

export const updateApplication = (data: {
  applicationNumber: string;
  comment: string;
  contactName: string;
  contactPhone: string;
  criticalLevelId: number;
  realEstateGuid: string;
  roomGuid: string;
  defectTypeId: number;
  executorId: string;
  executorPositionId: number;
  language: string;
  ratingNps: number;
  unitDimensionId?: number;
  unitDimensionVolume?: string;
  photoList?: (File | string)[];
  checked: boolean;
  capex: ICapexApplication | null;
  isCapex: boolean;
  serviceTypeIds: number[];
  remoteAppealTypeId?: number;
}) => {
  return instance()
    .post<IHistoryModel>('/application/v6/update', data)
    .then((response) => response.data);
};

export const checkApplicationEditLimit = (applicationNumber: string) => {
  return instance()
    .get<void>('/application/editLimit/check', { params: { applicationNumber } })
    .then((response) => response.data);
};

export const checkApplication = (applicationNumber: string) => {
  return instance()
    .post<boolean>('/application/toggleChecked', { applicationNumber })
    .then((response) => response.data);
};

export const toggleObserver = (applicationNumber: string, observe: boolean) => {
  return instance()
    .post<boolean>('/application/observer/toggle', { applicationNumber, observe })
    .then((response) => response.data);
};

export const getObserverCandidates = (applicationNumber: string, search: string, page: number) => {
  return instance()
    .post<IApplicationObserverCandidate[]>('/application/observer/candidates', { applicationNumber, search, page })
    .then((response) => response.data);
};

export const addObservers = (applicationNumber: string, observers: { userId: string; positionId: number }[]) => {
  return instance()
    .post<IApplicationObserver[]>('/application/observer/add', { applicationNumber, observers })
    .then((response) => response.data);
};

export const removeObserver = (applicationNumber: string, userId: string) => {
  return instance()
    .post<IApplicationObserver[]>('/application/observer/remove', { applicationNumber, userId })
    .then((response) => response.data);
};

export const loadIsObserver = (applicationNumber: string) => {
  return instance()
    .post<boolean>('/application/observer/isObserver', { applicationNumber })
    .then((response) => response.data);
};

export const getObservers = (applicationNumber: string) => {
  return instance()
    .post<IApplicationObserver[]>('/application/observer/list', { applicationNumber })
    .then((response) => response.data);
};

export const markAsPuplicApplication = (applicationNumber: string, isPublic: boolean) => {
  return instance()
    .post('/application/update/public', { applicationNumber, isPublic })
    .then((response) => response.data);
};

export const updateApplicationDefectType = (data: {
  applicationNumber: string;
  defectTypeId: number;
  executorId: string;
  executorPositionId: number;
  realEstateGuid: string;
  roomGuid: string;
  capex: ICapexApplication | null;
  isCapex: boolean;
  serviceTypeIds: number[];
}) => {
  return instance()
    .post<IHistoryModel>('/application/v5/updateDefectType', data)
    .then((response) => response.data);
};

export const changeApplicationStatus = (data: any) => {
  return instance()
    .post<IHistoryModel>('/application/v3/changeStatusId', data)
    .then((response) => response.data);
};

export const takeApplicationToWork = (data: {
  applicationNumber: string;
  comment?: string;
  clientCallTimestamp?: string;
}) => {
  return instance()
    .post<IHistoryModel>('/application/v4/changeStatus/inProgress', data)
    .then((response) => response.data);
};

export const getDefectType = (defectTypeId: number) => {
  return instance()
    .post<IDefectModel>('/defectType/load', { defectTypeId })
    .then((response) => response.data);
};

export const getStatuses = () => {
  return instance()
    .post('/dictionary/status', {})
    .then((response) => response.data);
};

export const getApplication = (applicationHeaderId: number) => {
  return instance()
    .post(`/application/load`, { applicationHeaderId })
    .then((response) => response.data);
};

export const getExecutorsForChange = (applicationNumber: string) => {
  return instance()
    .get<IExecutorModel[]>('/executor/listExecutors/' + applicationNumber)
    .then((response) => response.data);
};

export const getImageInfo = (imageName: string) => {
  return instance()
    .post<{ downloadOptimizedUrl: string; downloadThumbnailUrl: string; downloadUrl: string }[]>(
      '/file/getDownloadURL',
      [imageName],
    )
    .then((response) => response.data);
};

export const saveCallback = (data: ICallback) => {
  return instance()
    .post('/callcenter/saveCallback', data)
    .then((response) => response.data);
};

export const checkCallbacks = (callbackTimestamp: Date) => {
  return instance()
    .post('/callcenter/checkCallbacks', { callbackTimestamp })
    .then((response) => response.data);
};

export const getDoneResponses = () => {
  return instance()
    .post<IDoneResponse[]>('/dictionary/doneResponse')
    .then((response) => response.data);
};

export const getDelayReasons = (defectTypeId?: number) => {
  return instance()
    .post<IDelayReason[]>('/dictionary/delayReason', { defectTypeId })
    .then((response) => response.data);
};

export const loadRealEstate = (guid: string) => {
  return instance()
    .post<IRealEstateModel>('/dictionary/realEstate', { guid })
    .then((response) => response.data);
};

export const loadSmartRemont = (config?: AxiosRequestConfig) => {
  return instance()
    .get<ISmartRemont | null>('/smartRemont/load', config)
    .then((response) => response.data);
};

export const loadRealEstateDefectType = (config?: AxiosRequestConfig) => {
  return instance()
    .get<IRealEstateDefectType>('/realEstateDefectType/load', config)
    .then((response) => response.data);
};

export const checkIfHasPublicApplications = (config?: AxiosRequestConfig) => {
  return instance()
    .get<{ hasApplicationIsPublic: boolean }>('/application/hasApplicationIsPublic', config)
    .then((response) => response.data);
};

export const getCapexConstructiveList = () => {
  return instance()
    .post<ICapexConstructive[]>('/capex/constructive/listAllNotDeleted')
    .then((response) => response.data);
};

export const changeCapexProgress = (applicationNumber: string, capexProgress: ICapexProgress) => {
  return instance()
    .post('/application/progressCapex', { applicationNumber, ...capexProgress })
    .then((response) => response.data);
};

export const getClientRatesByExecutor = (config?: AxiosRequestConfig) => {
  return instance()
    .get<IRateClientByExecutor[]>('/application/listMarkAndCommentFromExecutor', config)
    .then((response) => response.data);
};

export const rateClientByExecutor = (applicationNumber: string, data: IRateClientByExecutor) => {
  return instance()
    .post<IHistoryModel>('/application/markAndCommentFromExecutor', { applicationNumber, ...data })
    .then((response) => response.data);
};

export const sendToSignUsoApplication = (data: ISendToSignUsoApplication) => {
  return instance()
    .post('/application/uso/changeStatus', data)
    .then((response) => response.data);
};

export const rejectUsoApplication = (data: IRejectUsoApplication) => {
  return instance()
    .post('/application/uso/changeStatus', data)
    .then((response) => response.data);
};

export const revokeUsoApplication = (data: IChangeUsoApplicationStatus) => {
  return instance()
    .post('/application/uso/changeStatus', data)
    .then((response) => response.data);
};

export const checkIsCheckListClosed = (applicationNumber: string) => {
  return instance()
    .post<boolean>('/application/validCheckList/' + applicationNumber)
    .then((response) => response.data);
};

export const getNPSScore = (data: { phone: string; realEstateGuid: string }) => {
  return instance()
    .post<INPSScore[]>('/application/npsScore', data)
    .then((response) => response.data);
};
